import { forwardRef } from "react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { ArrowBackIcon } from "../../lib/icons";

export interface ScreenHeaderProps {
  title: ReactNode;
  /** Renders a ghost chevron-left back button when provided. */
  onBack?: () => void;
  /** Trailing action nodes, laid out end-aligned. */
  actions?: ReactNode;
  className?: string;
}

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      aria-label="Go back"
      className="flex size-10 shrink-0 items-center justify-center rounded-lg text-on-inverse transition-colors outline-none hover:bg-surface-overlay active:bg-surface-overlay focus-visible:ring-2 focus-visible:ring-border-focused"
    >
      <ArrowBackIcon aria-hidden="true" className="size-5" />
    </button>
  );
}

/**
 * Sticky inverse screen title bar — mode-locked dark chrome in both themes.
 */
export const ScreenHeader = forwardRef<HTMLElement, ScreenHeaderProps>(
  ({ title, onBack, actions, className }, ref) => {
    return (
      <header
        ref={ref}
        className={cn(
          "sticky top-0 z-(--z-nav) flex h-14 items-center bg-surface-inverse px-2 text-on-inverse",
          className,
        )}
      >
        {onBack ? <BackButton onBack={onBack} /> : null}
        <h1 className="type-h6 pointer-events-none absolute inset-x-14 truncate text-center">
          {title}
        </h1>
        {actions ? (
          <div className="ml-auto flex shrink-0 gap-1">{actions}</div>
        ) : null}
      </header>
    );
  },
);
ScreenHeader.displayName = "ScreenHeader";
